import axios from 'axios';
import { pickBy } from 'lodash';
import qs from 'qs';
import { FSC_SOURCE, FUEL_SURCHARGE_TYPE } from 'constants/fsc/fsc';
import client from './rest/AdminClient';

export const fscPeriodType = {
  CURRENT: 'current',
  UPCOMING: 'upcoming',
  EXPIRED: 'expired',
};

let isNewFscFlowCached = null;

export const createFscSettings = async (data, overrideIntersecting = false) => {
  try {
    const res = await client({
      method: 'POST',
      path: `/api/v2/fsc-configs?overrideIntersecting=${overrideIntersecting}`,
      entity: JSON.stringify(pickBy(data, (value) => value)),
    });
    return res.entity?.fscConfig;
  } catch (e) {
    let message;
    if (e?.entity?.error) {
      const splitted = e?.entity?.error.split(':');
      message = splitted[splitted.length - 1];
    } else {
      message = e?.message;
    }
    throw new Error(message);
  }
};

export const getFscSettingsById = async (id) => {
  try {
    const response = await client({
      method: 'GET',
      path: `/api/v2/fsc-configs/${id}`,
    });

    return mapEntityToModel(response?.entity);
  } catch (e) {
    throw new Error(e.entity?.error || e.message);
  }
};

export const getFscSettings = async (
  accountId,
  fscPeriodType,
  { perPage = 1000, skip, orderType } = {},
) => {
  try {
    const query = { accountId, fscPeriodType, perPage, skip, orderType };

    const response = await client({
      method: 'GET',
      path: `/api/v2/fsc-configs?${qs.stringify(query)}`,
    });

    return {
      ...response?.entity,
      rows: response?.entity?.rows.map((r) => mapEntityToModel(r)),
    };
  } catch (e) {
    throw new Error(e.entity?.error || e.message);
  }
};

export const updateFscSettings = async (
  id,
  data,
  overrideIntersecting = false,
) => {
  try {
    const res = await client({
      method: 'PUT',
      path: `/api/v2/fsc-configs/${id}?overrideIntersecting=${overrideIntersecting}`,
      entity: JSON.stringify(pickBy(data, (value) => value)),
    });
    return res.entity?.fscConfig;
  } catch (e) {
    let message;
    if (e?.entity?.error) {
      const splitted = e?.entity?.error.split(':');
      message = splitted[splitted.length - 1];
    } else {
      message = e?.message;
    }
    throw new Error(message);
  }
};

export const deleteFscSettings = (id) =>
  client({
    method: 'DELETE',
    path: `/api/v2/fsc-configs/${id}`,
  });

export const getCargomaticFscConfigTemplate = async (accountType) => {
  const url = `${PRICING_SERVICE_HOST}/fsc-configs/templates?source=${FSC_SOURCE.CARGOMATIC}&fuelSurchargeType=${FUEL_SURCHARGE_TYPE.PERCENT}&accountType=${accountType}`;
  const options = {
    method: 'get',
    url: url,
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': PRICING_SERVICE_API_KEY,
    },
  };
  const response = await axios(options);
  if (response.status !== 200) {
    throw new Error(
      `Error while getting cargomatic fsc config template for account type ${accountType}`,
    );
  }
  const { data } = response;
  return data;
};

export const getFscSettingAudits = async (
  accountId,
  page = 1,
  perPage = 100,
) => {
  try {
    const skip = (page - 1) * perPage;
    const query = { accountId, skip, perPage };
    const response = await client({
      method: 'GET',
      path: `/api/v2/fsc-configs/audit?${qs.stringify(query)}`,
    });

    return response?.entity?.rows || [];
  } catch (e) {
    throw new Error(e.entity?.error || e.message);
  }
};

export const getIsNewFscFlow = async () => {
  if (isNewFscFlowCached !== null) {
    return isNewFscFlowCached;
  }

  try {
    const response = await client({
      method: 'GET',
      path: '/api/v2/is-new-fsc-flow',
    });

    isNewFscFlowCached = response.entity?.isNewFscFlow;

    return isNewFscFlowCached;
  } catch (e) {
    throw new Error(e.entity?.error || e.message);
  }
};

const mapEntityToModel = (data) => ({
  ...data,
  importAnchorEvent: data.anchorEventImport,
  exportAnchorEvent: data.anchorEventExport,
  repositionAnchorEvent: data.anchorEventReposition,
  fuelPriceMin: data.matrixFuelPriceMin,
  fuelPriceMax: data.matrixFuelPriceMax,
  fuelPriceStepInterval: data.matrixFuelPriceStepInterval,
  fscMin: data.matrixFscMinPercent || data.matrixFscMinCostPerMile,
  fscStepInterval:
    data.matrixFscPercentStepInterval || data.matrixFscCostPerMileStepInterval,
});
